import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, ExternalLink, ShieldAlert } from 'lucide-react';
import * as React from 'react';
import { ApiError } from '@ps/api-client/http';
import type { Application, ApplicationDetail, DocumentRow, Hit, staffApi } from '@ps/api-client/staff';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Card, CardTitle, Details, Section } from '@ps/ui-kit/components/card';
import { Field, Select, Textarea } from '@ps/ui-kit/components/field';
import { FilterButton } from '@ps/ui-kit/components/filter-button';
import { Input } from '@ps/ui-kit/components/input';
import { Pill } from '@ps/ui-kit/components/pill';
import { SearchBar } from '@ps/ui-kit/components/search-bar';
import { Empty, Loading } from '@ps/ui-kit/components/states';
import { cn } from '@ps/ui-kit/lib/cn';
import { countryName, date, dateTime, money } from '@ps/ui-kit/lib/format';
import { useBlurValidation } from '@ps/ui-kit/lib/use-blur-validation';
import { minChars } from '@ps/ui-kit/lib/validate';
import { LinkRow, Table } from '@ps/ui-kit/components/table';
import { AuditEntries, fromAuditRow } from '../audit/audit-entries';
import { MatchComparison, MatchSummary, ScreeningReports, VendorDetails } from './screening-detail';
import { BAND_TONE, CHECKLIST, DOC_LABELS, listTypes, NFTF_MEASURES, RISK_FACTORS, VERIFY_TONE } from './labels';

// A-03 … A-08. Each tab reads the application the detail frame loaded; actions call the API and hand the refreshed
// application back (onUpdated), so the action rail always shows the server's current list.

type Api = ReturnType<typeof staffApi>;
export interface TabProps { api: Api; detail: ApplicationDetail; onUpdated: (d: ApplicationDetail) => void }
const errMsg = (e: unknown) => (e instanceof ApiError ? e.body.message : 'Something went wrong. Please try again.');

// ───────────── A-03 Profile ─────────────
export function ProfileTab({ detail }: TabProps) {
  const a = detail.application;
  const corporate = a.type === 'CORPORATE';
  const applicant = a.parties.find(p => p.isApplicant);
  const confirmedPep = a.hits.some(h => detail.pepOnlyHitIds?.includes(h.id) && (h.disposition === 'TRUE_MATCH' || h.mlroDecision === 'TRUE_MATCH'));
  return (
    <div className="space-y-4">
      <Section>
        <CardTitle eyebrow>{corporate ? 'Company' : 'Applicant'}</CardTitle>
        <Details rows={[
          ['Legal name', a.legalName],
          ...(corporate ? [['BR number', a.brNumberMasked], ['Incorporated', `${date(a.incorporationDate)} · ${applicant?.nationality ? countryName(applicant.nationality) : '—'}`]] as Array<[string, React.ReactNode]> : [['Date of birth', date(applicant?.dob)], ['Nationality', applicant?.nationality ? countryName(applicant.nationality) : null]] as Array<[string, React.ReactNode]>),
          ['Country', countryName(a.country)], ['Industry', a.industry], ['Website', a.website], ['Contact email', a.email],
          ['Channel', 'Digital — non-face-to-face'],
          ['PEP', a.pep ? <Pill tone="bad" upper>{a.pepDeclared ? 'Declared PEP' : confirmedPep ? 'PEP (confirmed match)' : 'PEP (screening)'}</Pill> : 'No'],
          ...(a.pepDetail ? [['Declared public role', a.pepDetail]] as Array<[string, string]> : []),
        ]} />
      </Section>
      <Section>
        <CardTitle eyebrow>Declared activity</CardTitle>
        <p className="mb-3 text-xs text-ink-3">Baselines for monitoring (TM-02 / TM-03 / TM-13). Read-only after submission; changed only through a request for information or an audited amendment.</p>
        <Details rows={[
          ['Expected monthly volume', money(a.declared.monthlyVolumeHkdMinor)], ['Payments per month', a.declared.txPerMonth],
          ['Corridors', a.declared.corridors.map(c => countryName(c)).join(', ')], ['Source of funds', a.declared.sourceOfFunds], ['Purpose', a.declared.purpose],
        ]} />
      </Section>
      {a.rfis.length > 0 && (
        <Section>
          <CardTitle eyebrow>Requests for information</CardTitle>
          <ul className="space-y-3">
            {a.rfis.map(r => (
              <li key={r.id} className="rounded-lg border border-line p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2"><Pill tone={r.status === 'OPEN' ? 'warn' : 'ok'} upper>{r.status}</Pill><span className="text-ink-3">sent {dateTime(r.requestedAt)} · due {date(r.dueDate)}</span></div>
                <p className="mt-2 text-ink">{r.items.join(' · ')}</p>
                <p className="mt-1 whitespace-pre-line text-ink-2"><span className="text-ink-3">To applicant: </span>{r.message}</p>
                {r.internalNote && <p className="mt-1 whitespace-pre-line text-ink-2"><span className="text-ink-3">Reviewer's note (internal): </span>{r.internalNote}</p>}
                {r.responseNote && <p className="mt-2 border-l-2 border-tick pl-3 text-ink">Applicant: {r.responseNote} <span className="text-ink-3">({dateTime(r.respondedAt)})</span></p>}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

// ───────────── A-04 Documents ─────────────
/** One group per document, newest version first; earlier versions stay available as evidence but are folded away. */
function latestFirst(rows: DocumentRow[]): DocumentRow[][] {
  const groups = new Map<string, DocumentRow[]>();
  for (const r of rows) groups.set(r.id, [...(groups.get(r.id) ?? []), r]);
  return [...groups.values()].map(g => [...g].sort((x, y) => y.versionNo - x.versionNo));
}

export function DocumentsTab({ api, detail, onUpdated }: TabProps) {
  const a = detail.application;
  const docs = useQuery({ queryKey: ['docs', a.id, a.version], queryFn: () => api.documents(a.id) });
  const checks = useQuery({ queryKey: ['idv', a.id, a.version], queryFn: () => api.identityChecks(a.id) });
  // The registry check runs by itself after a business submits (ADR-081); staff can run it again, e.g. after the
  // provider was unavailable. The server checks the role and the state.
  const qc = useQueryClient();
  const [kybBusy, setKybBusy] = React.useState(false);
  const [kybError, setKybError] = React.useState<string | null>(null);
  const canRunKyb = a.type === 'CORPORATE' && detail.availableActions.includes('DECIDE_DOCUMENT');
  async function runKyb() {
    setKybBusy(true); setKybError(null);
    try { await api.companyCheck(a.id); await qc.invalidateQueries({ queryKey: ['idv', a.id] }); }
    catch (e) { setKybError(errMsg(e)); }
    finally { setKybBusy(false); }
  }
  const [open, setOpen] = React.useState<DocumentRow | null>(null);
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) => setExpanded(x => { const n = new Set(x); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const canDecide = detail.availableActions.includes('DECIDE_DOCUMENT');
  const party = (id: string | null) => a.parties.find(p => p.id === id)?.name ?? (id ? '—' : 'Application');
  if (docs.isPending) return <Loading />;
  return (
    <div className="space-y-4">
      <Section>
        <CardTitle eyebrow>Documents</CardTitle>
        {!docs.data?.items.length ? <Empty title="No documents yet" /> : (
          <div className="overflow-x-auto">
            <Table bare tableClassName="[overflow-wrap:anywhere]">
              <thead><tr><th>Document</th><th>Party</th><th>Verification</th><th>Face match</th><th>Expires</th><th>Version</th></tr></thead>
              <tbody>
                {latestFirst(docs.data.items).map(([latest, ...earlier]) => (
                  <React.Fragment key={latest!.id}>
                    {[latest!, ...(expanded.has(latest!.id) ? earlier : [])].map((d, i) => (
                      <LinkRow key={`${d.id}-${d.versionNo}`} className={cn(i > 0 && 'text-ink-3', open?.id === d.id && open.versionNo === d.versionNo && 'bg-mint-2')} onOpen={() => setOpen(d)}>
                        <td className={cn(i === 0 ? 'font-semibold text-ink' : 'pl-8')}><button className="text-left hover:underline">{i === 0 ? (DOC_LABELS[d.docType] ?? d.docType) : 'Earlier version'}</button>{i === 0 && d.source === 'IDV_PROOF' && <span className="ml-2 text-xs text-ink-3">identity check</span>}</td>
                        <td>{party(d.partyId)}</td>
                        <td><Pill tone={VERIFY_TONE[d.verification] ?? 'neu'} upper>{d.verification.replaceAll('_', ' ')}</Pill></td>
                        <td className="tabular">{d.faceMatchScore ?? '—'}</td>
                        <td>{date(d.expiresOn)}</td>
                        <td className="tabular whitespace-nowrap">v{d.versionNo}
                          {i === 0 && earlier.length > 0 && (
                            <button className="ml-2 text-xs font-semibold text-tick hover:underline" aria-expanded={expanded.has(d.id)}
                              onClick={e => { e.stopPropagation(); toggle(d.id); }}>{expanded.has(d.id) ? 'Hide earlier' : `+${earlier.length} earlier`}</button>
                          )}
                        </td>
                      </LinkRow>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>
      {open && <DocumentViewer api={api} detail={detail} doc={open} canDecide={canDecide} onUpdated={d => { onUpdated(d); setOpen(null); }} />}
      <Section>
        <CardTitle eyebrow aside={canRunKyb && <Button size="sm" variant="secondary" disabled={kybBusy} onClick={() => void runKyb()}>{kybBusy ? 'Starting…' : 'Run company check'}</Button>}>Identity checks</CardTitle>
        {kybError && <Alert tone="bad" className="mb-3">{kybError}</Alert>}
        {!checks.data?.items.length ? <p className="text-sm text-ink-3">No identity checks started. A business's registry check starts by itself after submission; the online identity check is asked for through Request information.</p> : (
          <ul className="divide-y divide-line text-sm">
            {checks.data.items.map(c => (
              <li key={c.id} className="flex flex-wrap items-center gap-3 py-2">
                <Pill tone={VERIFY_TONE[c.status] ?? 'neu'} upper>{c.status.replaceAll('_', ' ')}</Pill>
                <span className="font-semibold text-ink">{c.kind} · {party(c.partyId)}</span>
                <span className="font-mono text-xs text-ink-3">{c.vendorReference}</span>
                {c.declinedCodes.length > 0 && <span className="text-bad">declined: {c.declinedCodes.join(', ')}</span>}
                <span className="ml-auto text-ink-3">{dateTime(c.completedAt ?? c.requestedAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function DocumentViewer({ api, detail, doc, canDecide, onUpdated }: { api: Api; detail: ApplicationDetail; doc: DocumentRow; canDecide: boolean; onUpdated: (d: ApplicationDetail) => void }) {
  const a = detail.application;
  // Every open is audited (DOC_VIEW): the file and its details are fetched when the viewer opens, never prefetched.
  const file = useQuery({ queryKey: ['doc-file', doc.id, doc.versionNo], queryFn: () => api.documentFile(a.id, doc.id, doc.versionNo), staleTime: Infinity, gcTime: 0 });
  const info = useQuery({ queryKey: ['doc-info', doc.id, doc.versionNo], queryFn: () => api.documentDetails(a.id, doc.id, doc.versionNo), staleTime: Infinity, gcTime: 0 });
  const url = React.useMemo(() => (file.data ? URL.createObjectURL(file.data) : null), [file.data]);
  React.useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const [mode, setMode] = React.useState<'REJECT' | 'REQUEST_REPLACEMENT' | null>(null);
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const v = useBlurValidation({ reason: () => (mode ? minChars(reason, 20) : null) });
  async function decide(decision: 'ACCEPT' | 'REJECT' | 'REQUEST_REPLACEMENT') {
    setBusy(true); setError(null);
    try { onUpdated(await api.decideDocument(a.id, doc.id, doc.versionNo, { decision, reason: reason || undefined, version: a.version })); }
    catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  }
  const undecided = !doc.decidedAt;
  return (
    <Section>
      <CardTitle eyebrow aside={<span className="text-xs text-ink-3">SHA-256 {doc.sha256.slice(0, 12)}… · opening is recorded in the audit trail</span>}>{DOC_LABELS[doc.docType] ?? doc.docType} · v{doc.versionNo}</CardTitle>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-h-64 overflow-hidden rounded-lg border border-line bg-canvas">
          {file.isPending ? <Loading label="Opening…" /> : file.error ? <Alert tone="bad">{errMsg(file.error)}</Alert> : url && (
            doc.mimeType === 'application/pdf'
              ? <iframe title={doc.fileName} src={url} className="h-[28rem] w-full" />
              : <img src={url} alt={`${DOC_LABELS[doc.docType] ?? doc.docType} uploaded as ${doc.fileName}`} className="mx-auto max-h-[28rem] object-contain" />
          )}
          {url && <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 p-2 text-xs font-semibold text-tick-ink"><ExternalLink className="size-3" aria-hidden />Open in a new tab</a>}
        </div>
        <div className="space-y-3 text-sm">
          <Details rows={[
            ['File', doc.fileName], ['Uploaded', dateTime(doc.uploadedAt)], ['Source', doc.source === 'IDV_PROOF' ? `Identity check ${doc.vendorRef ?? ''}` : 'Uploaded by applicant'],
            ['Verification', <Pill key="v" tone={VERIFY_TONE[doc.verification] ?? 'neu'} upper>{doc.verification.replaceAll('_', ' ')}</Pill>],
            ...(doc.decidedAt ? [['Decision', `${doc.decisionReason ?? ''} (${dateTime(doc.decidedAt)})`]] as Array<[string, string]> : []),
          ]} />
          {doc.vendorResult && <div><p className="font-semibold text-ink-2">Vendor per-check results</p><pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-canvas p-2 text-xs">{JSON.stringify(doc.vendorResult, null, 2)}</pre></div>}
          {info.data?.extracted && <div><p className="font-semibold text-ink-2">Extracted fields</p><pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-canvas p-2 text-xs">{JSON.stringify(info.data.extracted, null, 2)}</pre></div>}
          {canDecide && undecided && (
            <div className="space-y-2 border-t border-line pt-3">
              {error && <Alert tone="bad">{error}</Alert>}
              {mode ? (
                <>
                  <Field id="doc-reason" label={mode === 'REJECT' ? 'Reason for rejecting (at least 20 characters)' : 'What is wrong with this copy (internal, at least 20 characters)'} error={v.errorOf('reason')}>
                    <Textarea id="doc-reason" value={reason} onChange={e => setReason(e.target.value)} {...v.field('reason', 'doc-reason')} />
                  </Field>
                  {<p className="text-xs text-ink-3">{doc.source === 'IDV_PROOF'
                    ? 'This copy came from the identity check, so the applicant is asked to do the identity check again: "Please complete the identity check again."'
                    : `The applicant receives a neutral request: "Please upload a new copy of your ${(DOC_LABELS[doc.docType] ?? '').toLowerCase()}."`}</p>}
                  <div className="flex gap-2"><Button size="sm" variant={mode === 'REJECT' ? 'destructive' : 'primary'} disabled={busy || reason.trim().length < 20} onClick={() => decide(mode)}>Confirm</Button><Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button></div>
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(doc.verification === 'PENDING' || doc.verification === 'MANUAL_REVIEW') && <Button size="sm" onClick={() => decide('ACCEPT')} disabled={busy}>Accept</Button>}
                  <Button size="sm" variant="secondary" onClick={() => setMode('REQUEST_REPLACEMENT')}>{doc.source === 'IDV_PROOF' ? 'Request new identity check' : 'Request replacement'}</Button>
                  <Button size="sm" variant="destructive" onClick={() => setMode('REJECT')}>Reject</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

// ───────────── A-05 Screening ─────────────
const hitTone = (h: Hit) => (!h.disposition ? 'bad' : h.disposition === 'FALSE_POSITIVE' ? 'ok' : h.disposition === 'TRUE_MATCH' ? 'bad' : 'warn');
/** Open first, then the highest score, then the name: the most likely match is reviewed first. */
const byPriority = (x: Hit, y: Hit) => Number(!!x.disposition) - Number(!!y.disposition) || y.score - x.score || x.listedName.localeCompare(y.listedName);

export function ScreeningTab({ api, detail, onUpdated }: TabProps) {
  const a = detail.application;
  const sorted = React.useMemo(() => [...a.hits].sort(byPriority), [a.hits]);
  const open = sorted.filter(h => !h.disposition);
  const [show, setShow] = React.useState<'open' | 'all'>(open.length ? 'open' : 'all');
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState<string | null>(open[0]?.id ?? sorted[0]?.id ?? null);
  if (!a.hits.length) return <Empty title="No possible matches">{a.state === 'SUBMITTED' && a.screeningFailedAt ? 'Screening could not complete; retry it from the action rail.' : 'Every party was screened and none matched at or above the threshold.'}</Empty>;

  const term = search.trim().toLowerCase();
  const rows = sorted.filter(h => (show === 'all' || !h.disposition)
    && (!term || `${h.partyName} ${h.listedName} ${listTypes(h.types)} ${countryName(String(h.listedAttributes.nationality ?? '')) ?? ''}`.toLowerCase().includes(term)));
  const hit = a.hits.find(h => h.id === selected) ?? rows[0];
  const pos = hit ? rows.findIndex(h => h.id === hit.id) : -1;
  const go = (i: number) => { const h = rows[i]; if (h) setSelected(h.id); };
  // After a disposition, move on to the next open match (the list order is the review order).
  const updated = (d: ApplicationDetail) => {
    const next = [...d.application.hits].sort(byPriority).find(h => !h.disposition);
    if (next) setSelected(next.id);
    onUpdated(d);
  };

  return (
    <div className="@container space-y-4">
      <ScreeningReports api={api} applicationId={a.id} canRefresh={detail.canRefreshScreening ?? false} />
      <CardTitle eyebrow>Possible matches · {open.length ? `${open.length} open of ${sorted.length}` : `all ${sorted.length} dispositioned`}</CardTitle>
      {sorted.length > 1 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <FilterButton on={show === 'open'} onClick={() => setShow('open')}>Open ({open.length})</FilterButton>
            <FilterButton on={show === 'all'} onClick={() => setShow('all')}>All ({sorted.length})</FilterButton>
            <SearchBar label="Search possible matches" placeholder="Search name, list or nationality" value={search} onChange={setSearch} className="h-9 min-w-56 flex-1" />
          </div>
          {rows.length === 0 ? <p className="text-sm text-ink-3">{show === 'open' && !term ? 'Every possible match has a disposition.' : 'No possible match fits this search.'}</p> : (
            <Table bare compact label="Possible matches" className="max-h-80 overflow-y-auto rounded-lg border border-line">
              <thead className="sticky top-0 z-10 bg-surface">
                <tr><th>Party</th><th>Listed name</th><th className="text-right">Score</th><th>Lists</th><th>Date of birth</th><th>Nationality</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map(h => (
                  <LinkRow key={h.id} onOpen={() => setSelected(h.id)} className={cn(h.id === hit?.id && 'bg-mint-2')} aria-selected={h.id === hit?.id}>
                    <td className="whitespace-nowrap">{h.partyName}</td>
                    <td><button className="text-left font-semibold text-ink hover:underline" onClick={e => { e.stopPropagation(); setSelected(h.id); }}>{h.listedName}</button></td>
                    <td className="text-right tabular font-semibold">{h.vendorNoScore ? '—' : h.score}</td>
                    <td>{listTypes(h.types) || '—'}</td>
                    <td className="whitespace-nowrap">{h.listedAttributes.dob ? date(String(h.listedAttributes.dob)) : '—'}</td>
                    <td>{countryName(String(h.listedAttributes.nationality ?? '')) ?? '—'}</td>
                    <td><Pill tone={hitTone(h)} upper>{h.disposition ? h.disposition.replaceAll('_', ' ') : 'OPEN'}</Pill></td>
                  </LinkRow>
                ))}
              </tbody>
            </Table>
          )}
          {hit && pos >= 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-ink-2">
              <span>Match <b className="tabular text-ink">{pos + 1}</b> of {rows.length}{show === 'open' ? ' open' : ''}</span>
              <span className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={pos <= 0} onClick={() => go(pos - 1)}>Previous</Button>
                <Button size="sm" variant="secondary" disabled={pos >= rows.length - 1} onClick={() => go(pos + 1)}>Next</Button>
              </span>
            </div>
          )}
        </>
      )}
      {hit && <HitPanel key={hit.id} api={api} detail={detail} hit={hit} onUpdated={updated} />}
    </div>
  );
}

type HitDisposition = keyof NonNullable<ApplicationDetail['hitReasonCodes']>;

function HitPanel({ api, detail, hit, onUpdated }: TabProps & { hit: Hit }) {
  const a = detail.application;
  const [disposition, setDisposition] = React.useState<HitDisposition | ''>('');
  const [reasonCode, setReasonCode] = React.useState('');
  const [rationale, setRationale] = React.useState('');
  const [evidence, setEvidence] = React.useState('');
  const [mlro, setMlro] = React.useState<'TRUE_MATCH' | 'FALSE_POSITIVE' | ''>('');
  // A PEP-only list entry: confirming it continues the application as a PEP instead of blocking (ADR-082).
  const pepOnly = detail.pepOnlyHitIds?.includes(hit.id) ?? false;
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const v = useBlurValidation({
    rationale: () => minChars(rationale, 80),
    evidence: () => (disposition === 'FALSE_POSITIVE' && !evidence.trim() ? 'Evidence is required to discount a match' : null),
  });
  // The outcome is chosen first and offers only its reason codes; an outcome with a single code has it chosen (ADR-105).
  const reasonsFor = (d: HitDisposition) => detail.hitReasonCodes?.[d] ?? [];
  const chooseDisposition = (d: HitDisposition) => {
    setDisposition(d);
    const codes = reasonsFor(d);
    setReasonCode(codes.length === 1 ? codes[0]! : codes.includes(reasonCode) ? reasonCode : '');
  };
  const canDispose = detail.availableActions.includes('DISPOSE_HIT') && !hit.disposition;
  const canMlro = detail.availableActions.includes('MLRO_DECIDE_HIT') && hit.disposition === 'ESCALATED_MLRO' && !hit.mlroDecision;

  async function run(fn: () => Promise<ApplicationDetail>) {
    setBusy(true); setError(null);
    try { onUpdated(await fn()); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  // Everything the provider sent about this match, with what we hold to compare it with (ADR-084).
  const vendor = useQuery({ queryKey: ['hit-detail', a.id, hit.id], queryFn: () => api.hitDetail(a.id, hit.id) });
  return (
    <div className="space-y-4">
      <MatchSummary hit={hit} detail={vendor.data?.detail ?? null} />
      {vendor.isPending ? <Loading label="Loading the match details…" /> : <MatchComparison app={a} hit={hit} detail={vendor.data?.detail ?? null} />}
      {vendor.data?.detail && <VendorDetails detail={vendor.data.detail} vendor="the screening provider" />}
      {vendor.data?.detail && !vendor.data.detail.detail && (
        <p className="text-sm text-ink-3">This match was screened before PayStreet kept the provider's full record, so only the basic details are shown.{' '}
          {detail.canRefreshScreening ? 'Use “Get full details from the provider” above to fetch the rest.' : 'An analyst or the Compliance Officer can fetch the rest.'}</p>
      )}
      {hit.disposition && (
        <div className="rounded-lg bg-canvas p-3 text-sm">
          <p className="font-semibold text-ink">{hit.disposition.replaceAll('_', ' ')} · {hit.reasonCode}</p>
          <p className="mt-1 whitespace-pre-line text-ink-2 [overflow-wrap:anywhere]">{hit.rationale}</p>
          {hit.evidenceRef && <p className="mt-1 text-ink-3 [overflow-wrap:anywhere]">Evidence: {hit.evidenceRef}</p>}
          <p className="mt-1 text-xs text-ink-3">{dateTime(hit.disposedAt)} · immutable</p>
          {hit.mlroDecision && <p className="mt-2 border-t border-line pt-2 text-ink">MLRO: {hit.mlroDecision.replaceAll('_', ' ')} — {hit.mlroRationale}</p>}
        </div>
      )}
      {error && <Alert tone="bad">{error}</Alert>}

      {canDispose && (
        <Card className="space-y-3 p-4" aria-label="Disposition">
          <CardTitle eyebrow>Disposition — Guideline ¶6.18</CardTitle>
          <fieldset>
            <legend className="sr-only">Outcome</legend>
            <div className="mt-1 flex flex-wrap gap-4 text-sm">
              {([['TRUE_MATCH', pepOnly ? 'Confirm true match — PEP, continues to CO approval' : 'Confirm true match — block'], ['FALSE_POSITIVE', 'Discount as false positive'], ['ESCALATED_MLRO', 'Escalate to MLRO']] as const).map(([v, l]) => (
                <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${disposition === v ? 'border-tick bg-mint-2 font-semibold text-ink' : 'border-line bg-surface hover:bg-canvas'}`}><input type="radio" name="disposition" checked={disposition === v} onChange={() => chooseDisposition(v)} />{l}</label>
              ))}
            </div>
          </fieldset>
          {disposition && (
            <Field id="reason-code" label="Reason code">
              <Select id="reason-code" value={reasonCode} onChange={e => setReasonCode(e.target.value)}><option value="">Choose…</option>{reasonsFor(disposition).map(c => <option key={c} value={c}>{c.replaceAll('_', ' ').toLowerCase()}</option>)}</Select>
            </Field>
          )}
          <Field id="rationale" label="Rationale" hint={`${rationale.trim().length} / 80 characters minimum`} error={v.errorOf('rationale')}><Textarea id="rationale" {...v.field('rationale', 'rationale')} value={rationale} onChange={e => setRationale(e.target.value)} /></Field>
          <Field id="evidence" label={`Evidence${disposition === 'FALSE_POSITIVE' ? ' (required)' : ''}`} hint="e.g. HKID verified against the Immigration record, document reference" error={v.errorOf('evidence')}>
            <Input id="evidence" value={evidence} onChange={e => setEvidence(e.target.value)} {...v.field('evidence', 'evidence')} />
          </Field>
          <Button disabled={busy || !disposition || !reasonCode} onClick={() => v.touchAll() && run(() => api.dispose(a.id, hit.id, { disposition: disposition as string, reasonCode, rationale, evidenceRef: evidence || undefined, version: a.version }))}>
            {busy ? 'Saving…' : 'Save disposition'}
          </Button>
          <p className="text-xs text-ink-3">A saved disposition cannot be changed and is recorded with your name.</p>
        </Card>
      )}

      {canMlro && (
        <Card className="space-y-3 p-4" aria-label="MLRO decision">
          <p className="flex items-center gap-2 font-semibold text-ink"><ShieldAlert className="size-4 text-warn" aria-hidden />MLRO decision on an escalated match</p>
          <div className="flex flex-wrap gap-4 text-sm">
            {([['TRUE_MATCH', pepOnly ? 'True match — PEP, continues to CO approval' : 'True match — block'], ['FALSE_POSITIVE', 'False positive']] as const).map(([v, l]) => (
              <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${mlro === v ? 'border-tick bg-mint-2 font-semibold text-ink' : 'border-line bg-surface hover:bg-canvas'}`}><input type="radio" name="mlro" checked={mlro === v} onChange={() => setMlro(v)} />{l}</label>
            ))}
          </div>
          <Field id="mlro-rationale" label="Rationale" hint={`${rationale.trim().length} / 80 characters minimum`} error={v.errorOf('rationale')}><Textarea id="mlro-rationale" {...v.field('rationale', 'mlro-rationale')} value={rationale} onChange={e => setRationale(e.target.value)} /></Field>
          <Button disabled={busy || !mlro} onClick={() => v.touchAll() && run(() => api.mlroDecide(a.id, hit.id, { decision: mlro as 'TRUE_MATCH' | 'FALSE_POSITIVE', rationale, version: a.version }))}>{busy ? 'Saving…' : 'Record decision'}</Button>
        </Card>
      )}
    </div>
  );
}

/** A compared value highlighted as in the prototype: green when it matches the list entry, amber when it differs. */
// ───────────── A-06 Risk ─────────────
export function RiskTab({ detail }: TabProps) {
  const r = detail.application.risk;
  if (!r) return <Empty title="Not assessed yet">Risk is assessed after screening and again when the application is recommended.</Empty>;
  return (
    <Section>
      <CardTitle eyebrow aside={<span className="flex items-center gap-2"><span className="font-display text-2xl font-bold tabular text-ink">{r.score}</span><span className="text-ink-3">/ 100</span><Pill tone={BAND_TONE[r.band] ?? 'neu'} upper>{r.band}</Pill></span>}>Customer risk assessment</CardTitle>
      <Table bare>
        <thead><tr><th>Factor</th><th className="text-right">Score</th><th className="text-right">Of</th></tr></thead>
        <tbody>
          {Object.entries(r.factors).map(([k, v]) => (
            <tr key={k} className="border-t border-line"><td>{RISK_FACTORS[k]?.label ?? k}</td><td className="text-right tabular font-semibold">{v}</td><td className="text-right tabular text-ink-3">{RISK_FACTORS[k]?.max ?? '—'}</td></tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-xs text-ink-3">Model {r.modelVersion}. Band override is Phase 2 (read-only in the MVP).</p>
      {r.band === 'HIGH' && (
        <Alert tone="warn" className="mt-4" title="Consequences of a HIGH band, applied automatically">
          Approval routes to the Compliance Officer only; EDD pack required; review every 12 months; enhanced monitoring parameters.
        </Alert>
      )}
    </Section>
  );
}

// ───────────── A-07 CDD checklist ─────────────
export function ChecklistTab({ api, detail, onUpdated }: TabProps) {
  const a = detail.application;
  const items = CHECKLIST.filter(c => a.type === 'CORPORATE' || !c.corporate);
  const can = detail.availableActions.includes('UPDATE_CHECKLIST');
  return (
    <Section>
      <CardTitle eyebrow>CDD checklist</CardTitle>
      <ul className="divide-y divide-line">
        {items.map(c => <ChecklistRow key={c.item} def={c} entry={a.checklist.find(e => e.item === c.item)} can={can && !c.system} api={api} detail={detail} onUpdated={onUpdated} />)}
      </ul>
      <p className="mt-3 text-xs text-ink-3">Screening and risk are completed by the system. <strong>Recommend</strong> appears in the action rail only when every applicable item is done and a non-face-to-face measure is chosen.</p>
    </Section>
  );
}

function ChecklistRow({ def, entry, can, api, detail, onUpdated }: { def: (typeof CHECKLIST)[number]; entry?: Application['checklist'][number]; can: boolean } & TabProps) {
  const [editing, setEditing] = React.useState(false);
  const [evidence, setEvidence] = React.useState(entry?.evidence ?? '');
  const [measure, setMeasure] = React.useState(entry?.nftfMeasure ?? '');
  const v = useBlurValidation({
    evidence: () => minChars(evidence, 5),
    measure: () => (def.item === 'NFTF_MEASURE' && !measure ? 'Choose the non-face-to-face measure' : null),
  });
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const done = !!entry?.done;
  async function save() {
    setBusy(true); setError(null);
    try { onUpdated(await api.checklist(detail.application.id, { item: def.item, evidence, nftfMeasure: def.item === 'NFTF_MEASURE' ? measure : undefined, version: detail.application.version })); setEditing(false); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start gap-3">
        {done ? <CheckCircle2 className="mt-0.5 size-5 text-ok" aria-label="Done" /> : <Circle className="mt-0.5 size-5 text-ink-3" aria-label="Outstanding" />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-ink">{def.label} <span className="ml-1 text-xs font-normal text-ink-3">{def.ref}</span></p>
          {entry?.evidence && !editing && <p className="text-sm text-ink-2">{entry.nftfMeasure ? `${NFTF_MEASURES.find(m => m.value === entry.nftfMeasure)?.label} — ` : ''}{entry.evidence}</p>}
          {def.system && !done && <p className="text-sm text-ink-3">Completed automatically.</p>}
        </div>
        {can && !editing && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>{done ? 'Update' : 'Complete'}</Button>}
      </div>
      {editing && (
        <div className="mt-3 space-y-2 pl-8">
          {def.item === 'NFTF_MEASURE' && (
            <Field id={`m-${def.item}`} label="Measure" error={v.errorOf('measure')}><Select id={`m-${def.item}`} value={measure} onChange={e => setMeasure(e.target.value)} {...v.field('measure', `m-${def.item}`)}><option value="">Choose one…</option>{NFTF_MEASURES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</Select></Field>
          )}
          <Field id={`e-${def.item}`} label="Evidence" error={v.errorOf('evidence')}><Input id={`e-${def.item}`} value={evidence} onChange={e => setEvidence(e.target.value)} {...v.field('evidence', `e-${def.item}`)} /></Field>
          {error && <Alert tone="bad">{error}</Alert>}
          <div className="flex gap-2"><Button size="sm" onClick={save} disabled={busy || evidence.trim().length < 5 || (def.item === 'NFTF_MEASURE' && !measure)}>Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button></div>
        </div>
      )}
    </li>
  );
}

// ───────────── A-08 Audit trail ─────────────
export function AuditTab({ api, detail }: TabProps) {
  const a = detail.application;
  const audit = useQuery({ queryKey: ['audit', a.id, a.version], queryFn: () => api.audit(a.id) });
  if (audit.isPending) return <Loading />;
  return (
    <Section>
      <CardTitle eyebrow>Audit trail</CardTitle>
      <AuditEntries entries={(audit.data?.items ?? []).map(fromAuditRow)} />
      <p className="mt-3 text-xs text-ink-3">Every entry carries the hash of its predecessor; editing any entry breaks the chain from that point. Chain verification is on the compliance console home (K-01).</p>
    </Section>
  );
}
